<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import type { NumberFormat } from '@/typings/NumberFormat';

const { locale: i18nLocale } = useI18n();

const props = defineProps<{
  min?: number
  max?: number
  step?: number
  format?: NumberFormat
  currencySymbol?: string
  decimals?: number
  disabled?: boolean
  placeholder?: string
  locale?: string
  integer?: boolean
}>();

const model = defineModel<number | undefined>({
  default: undefined,
});

const formattedValue = defineModel<string>('formattedValue', {
  default: '',
});

const displayValue = ref('');
const isFocused = ref(false);

const activeLocale = computed(() => props.locale || i18nLocale.value);
const inputType = computed(() => {
  return isFocused.value ? 'number' : 'text';
});

// Regroupement des fonctions
function formatValue(value: number | undefined): string {
  if (value === undefined) return '';
  
  switch (props.format) {
  case 'currency':
    return formatCurrencyWithoutDecimal(value, activeLocale);
    
  case 'percentage':
    return new Intl.NumberFormat(activeLocale.value, {
      style: 'percent',
      minimumFractionDigits: props.decimals ?? 0,
      maximumFractionDigits: props.decimals ?? 0
    }).format(value / 100);
    
  case 'number':
  default:
    return new Intl.NumberFormat(activeLocale.value, {
      minimumFractionDigits: props.decimals ?? 0,
      maximumFractionDigits: props.decimals ?? 0
    }).format(value);
  }
}

function parseValue(input: string): number | undefined {
  let cleanedValue = input;
  
  if (activeLocale.value.startsWith('fr')) {
    cleanedValue = cleanedValue.replace(/\./g, '').replace(',', '.');
  }
  
  cleanedValue = cleanedValue.replace(/[^\d.-]/g, '');
  
  if (!cleanedValue) return undefined;
  
  const parsedValue = parseFloat(cleanedValue);
  return isNaN(parsedValue) ? undefined : parsedValue;
}

function handleInput(event: Event) {
  const input = (event.target as HTMLInputElement);
  let value = input.value;

  if (props.integer && (event as InputEvent).data === '.') {
    value = value.replace(/\./g, '');
    (event.target as HTMLInputElement).value = value;
  }
  
  let parsed: number | undefined;
  
  if (inputType.value === 'number') {
    parsed = value === '' ? undefined : Number(value);
  } else {
    parsed = parseValue(value);
  }
  
  displayValue.value = value;
  
  if (parsed !== undefined) {
    let constrained = parsed;
    
    if (props.min !== undefined && parsed < props.min) {
      constrained = props.min;
    } else if (props.max !== undefined && parsed > props.max) {
      constrained = props.max;
    }
    
    if (props.integer) {
      constrained = Math.floor(constrained);
    }
    
    model.value = constrained;
  } else {
    model.value = undefined;
  }
  
  formattedValue.value = model.value !== undefined 
    ? formatValue(model.value) 
    : '';
}

function handleBlur() {
  isFocused.value = false;
  displayValue.value = model.value !== undefined 
    ? formatValue(model.value) 
    : '';
}

function handleFocus() {
  isFocused.value = true;
  
  if (model.value !== undefined) {
    displayValue.value = props.integer 
      ? String(Math.floor(model.value))
      : String(model.value);
  } else {
    displayValue.value = '';
  }
}

// Watchers
watch(model, (newValue) => {
  if (!isFocused.value) {
    displayValue.value = formatValue(newValue);
  }
  formattedValue.value = formatValue(newValue);
});

watch(activeLocale, () => {
  if (!isFocused.value) {
    displayValue.value = formatValue(model.value);
  }
  formattedValue.value = formatValue(model.value);
});

// Initialisation
displayValue.value = formatValue(model.value);
formattedValue.value = formatValue(model.value);
</script>

<template>
  <input :value="displayValue"
         :type="inputType"
         :placeholder="placeholder"
         :disabled="disabled"
         :min="min"
         :max="max"
         :step="step"
         class="input-numeric"
         :data-value-type="format"
         @input="handleInput"
         @blur="handleBlur"
         @focus="handleFocus" />
</template>
